import { useEffect, useMemo, useState } from "react";
import useTranslation from "../context/TranslationContext";
import api from "../backendAPI";
import { DropdownValuesData } from "../backendAPI/Dropdowns";

export type Filter = {
  label: string;
  fieldName: string;
  options: { id: string | number; name: string }[];
};

const EMPTY = (fieldName = ""): Filter => ({
  label: "",
  fieldName,
  options: [],
});

const decodeHtml = (str: string): string => {
  if (typeof document === "undefined") return str; // SSR guard
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  return txt.value;
};

const removeAllOption = (filter: Filter): Filter => ({
  ...filter,
  options: filter.options.filter((o) => String(o.id) !== "-2"),
});

export default function useDropdownValues({
  forProfile = false,
}: { forProfile?: boolean } = {}) {
  const { locale, i } = useTranslation();

  const [data, setData] = useState<DropdownValuesData>({});
  const [heightFilterOptions, setHeightFilterOptions] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    api.dropdowns
      .getDropdownValues(locale)
      .then((res) => {
        setData(res.data.filters);
      })
      .catch((err) => {
        console.error("[useDropdownValues] API call failed:", err);
      });
  }, [locale]);

  useEffect(() => {
    api.general.getRegistrationQuestions().then((res) => {
      const options = res.data.heightSliderValues.map((x) => ({
        id: x.id,
        name: x.description,
      }));
      setHeightFilterOptions(options);
    });
  }, []);

  return useMemo(() => {
    // API keys are all lowercase (e.g. "alcoholfilter")
    const get = (key: string, fieldName: string): Filter => {
      const raw = data[key.toLowerCase()];
      if (!raw) return EMPTY(fieldName);
      const filter: Filter = {
        label: decodeHtml(raw.label),
        fieldName: raw.fieldName,
        options: raw.options.map((o) => ({
          id: o.id,
          name: decodeHtml((o as any).description ?? (o as any).name),
        })),
      };
      return forProfile ? removeAllOption(filter) : filter;
    };

    const heightFilter: Filter = {
      label: i("Height"),
      fieldName: "tailleId",
      options: heightFilterOptions,
    };

    return {
      alcoholFilter: get("alcoholFilter", "alcoolId"),
      carFilter: get("carFilter", "voitureId"),
      currentChildrenFilter: get("currentChildrenFilter", "enfantActuelId"),
      desiredChildrenFilter: get("desiredChildrenFilter", "enfantDesiresId"),
      distanceFilter: get("distanceFilter", "distanceId"),
      drugsFilter: get("drugsFilter", "drogueId"),
      ethnicityFilter: get("ethnicityFilter", "ethnieId"),
      eyeColorFilter: get("eyeColorFilter", "yeuxId"),
      genderFilter: get("genderFilter", "genderId"),
      hairColorFilter: get("hairColorFilter", "cheveuxId"),
      heightFilter,
      hereForFilter: get("hereForFilter", "hereForId"),
      lookingForFilter: get("lookingForFilter", "lookingForId"),
      maritalStatusFilter: get("maritalStatusFilter", "etatCivileId"),
      occupationFilter: get("occupationFilter", "occupationId"),
      physicalAppFilter: get("physicalAppFilter", "AppPhysiqueId"),
      religionFilter: get("religionFilter", "religionId"),
      smokerFilter: get("smokerFilter", "fumeurId"),
      willingToMoveForLoveFilter: get(
        "willingToMoveForLoveFilter",
        "willingToMoveForLoveId"
      ),
      zodiacSignFilter: get("zodiacSignFilter", "signeZodiaqueId"),
    };
  }, [data, heightFilterOptions, forProfile, i]);
}
